import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { IssueSummary, Project } from '@jisy/shared';
import { issuesQuery } from '../api/queries.js';
import { useDebounced } from '../lib/useDebounced.js';
import { Avatar, ParentPrefix, StatusBadge } from './Badges.js';
import { FilterPopover } from './FilterPopover.js';
import styles from './IssuePicker.module.css';

/**
 * Окно подбора задач в план: слева доступные задачи с поиском и отбором по разделам, справа
 * уже выбранные. Порядок выбранных задаёт порядок строк в плане.
 *
 * Завершённых задач в списке нет: выдача прячет их сама, пока не выбраны статусы, а здесь
 * фильтра по статусу нет — планировать закрытое незачем.
 */
export function IssuePicker({
  rangeLabel,
  alreadyInPlan,
  projects,
  onClose,
  onSubmit,
}: {
  rangeLabel: string;
  alreadyInPlan: string[];
  projects: Project[];
  onClose: () => void;
  onSubmit: (issueKeys: string[]) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [sections, setSections] = useState<string[]>([]);
  const [selected, setSelected] = useState<IssueSummary[]>([]);
  const debounced = useDebounced(search, 300);

  // Объект фильтра входит в ключ запроса — собираем его на набор значений, а не на перерисовку.
  const filter = useMemo(
    () => ({ q: debounced, projectIds: sections, sort: 'updated', dir: 'desc' as const }),
    [debounced, sections],
  );

  const issues = useInfiniteQuery(issuesQuery(filter));

  const rows = useMemo(
    () => issues.data?.pages.flatMap((page) => page.items) ?? [],
    [issues.data],
  );

  const inPlan = useMemo(() => new Set(alreadyInPlan), [alreadyInPlan]);
  const selectedKeys = useMemo(() => new Set(selected.map((item) => item.key)), [selected]);

  function toggle(issue: IssueSummary): void {
    setSelected((current) =>
      current.some((item) => item.key === issue.key)
        ? current.filter((item) => item.key !== issue.key)
        : [...current, issue],
    );
  }

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.modal} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>
              {t('picker.title', { range: rangeLabel })}
            </Dialog.Title>
            <div className={styles.spacer} />
            <Dialog.Close asChild>
              <button type="button" className={styles.closeButton} aria-label={t('issue.close')}>
                ✕
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.body}>
            <div className={styles.left}>
              <div className={styles.searchRow}>
                <div className={styles.searchField}>
                  <span className={styles.searchIcon} aria-hidden="true">
                    ⌕
                  </span>
                  <input
                    className={styles.searchInput}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t('table.search')}
                    aria-label={t('table.search')}
                    autoFocus
                  />
                </div>
                <FilterPopover
                  name={t('filters.section')}
                  options={projects.map((project) => ({ id: project.id, label: project.name }))}
                  selected={sections}
                  onChange={setSections}
                  searchable
                />

                <span className={styles.count}>
                  {t('picker.found', { count: issues.data?.pages[0]?.total ?? 0 })}
                </span>
              </div>

              <div className={styles.listHead} role="row">
                <span className={styles.headCell}>{t('table.columns.key')}</span>
                <span className={styles.headCell}>{t('table.columns.summary')}</span>
                <span className={`${styles.headCell} ${styles.headOptional}`}>
                  {t('table.columns.status')}
                </span>
                <span className={`${styles.headCell} ${styles.headOptional}`}>
                  {t('table.columns.assignee')}
                </span>
                <span />
              </div>

              <div className={styles.list}>
                {rows.map((issue) => {
                  const added = inPlan.has(issue.key);
                  const chosen = selectedKeys.has(issue.key);

                  return (
                    <div
                      className={`${styles.item} ${chosen ? styles.itemChosen : ''}`}
                      key={issue.key}
                      role="row"
                    >
                      <span className={`mono ${styles.itemKey}`}>{issue.key}</span>
                      <span className={`${styles.itemTitle} truncate`} title={issue.summary}>
                        {/* В плоском списке подзадача без своей задачи — ключ родителя впереди. */}
                        {issue.type?.subtask && issue.parent && (
                          <ParentPrefix parent={issue.parent} />
                        )}
                        {issue.summary}
                      </span>
                      <span className={styles.itemStatus}>
                        <StatusBadge status={issue.status} />
                      </span>
                      <span className={styles.itemAssignee}>
                        <Avatar user={issue.assignee} size={20} />
                        <span className="truncate">
                          {issue.assignee?.displayName ?? t('table.unassigned')}
                        </span>
                      </span>
                      <button
                        type="button"
                        className={styles.addButton}
                        onClick={() => toggle(issue)}
                        disabled={added}
                        title={added ? t('picker.alreadyInPlan') : undefined}
                      >
                        {added ? t('picker.inPlan') : chosen ? t('picker.remove') : t('picker.add')}
                      </button>
                    </div>
                  );
                })}

                {issues.hasNextPage && (
                  <button
                    type="button"
                    className={styles.more}
                    onClick={() => void issues.fetchNextPage()}
                    disabled={issues.isFetchingNextPage}
                  >
                    {issues.isFetchingNextPage ? t('table.loadingMore') : t('table.loadMore')}
                  </button>
                )}

                {rows.length === 0 && !issues.isPending && (
                  <p className={styles.empty}>{t('filters.empty')}</p>
                )}
              </div>
            </div>

            <div className={styles.right}>
              <div className={styles.rightHead}>
                <span className={styles.rightTitle}>{t('picker.selected')}</span>
                <span className={`mono ${styles.rightCount}`}>{selected.length}</span>
                <div className={styles.spacer} />
                {selected.length > 0 && (
                  <button type="button" className={styles.clear} onClick={() => setSelected([])}>
                    {t('picker.clear')}
                  </button>
                )}
              </div>

              <div className={styles.chosenList}>
                {selected.map((issue, index) => (
                  <div className={styles.chosen} key={issue.key}>
                    <span className={`mono ${styles.chosenNumber}`}>{index + 1}</span>
                    <span className={`mono ${styles.chosenKey}`}>{issue.key}</span>
                    <span className={`${styles.chosenTitle} truncate`}>{issue.summary}</span>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => toggle(issue)}
                      aria-label={t('picker.remove')}
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {selected.length === 0 && <p className={styles.empty}>{t('picker.emptySelection')}</p>}
              </div>

              <div className={styles.footer}>
                <span className={styles.footerHint}>{t('picker.orderHint')}</span>
                <button type="button" className={styles.cancelButton} onClick={onClose}>
                  {t('issue.cancel')}
                </button>
                <button
                  type="button"
                  className={styles.submitButton}
                  disabled={selected.length === 0}
                  onClick={() => onSubmit(selected.map((issue) => issue.key))}
                >
                  {t('picker.submit', { count: selected.length })}
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
